import { TablePaginationConfig } from "antd";
import { useState } from "react";

import LocalStorageUtil from "../util/LocalStorageUtil";
import { LocalStorageConstants } from "../constants/ApplicationConstants/LocalStorageConstants";
import { General } from "../util/General";

import type { UserInterface } from "../shared/interface/auth/User.interface";

const useTablePagination = (rowLength: number) => {
  /* CONSTANTS */
  const userData = LocalStorageUtil.localStorageGetItem(LocalStorageConstants.USER_DATA) as UserInterface;
  const paginationConfig = userData?.config?.pagination;

  if (!paginationConfig) {
    throw new Error("Pagination configuration is missing from user data.");
  }

  const showTotal = (total: number, range: number[]) => (
    <span>
      {range[0]}-{range[1]} of
      <span style={{ margin: "0 5px", fontWeight: "bolder" }}>{total ? General.formatCount(total) : 0}</span>records
    </span>
  );

  /* STATE */
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: paginationConfig?.DEFAULT_PAGE_SIZE,
    total: rowLength,
    showSizeChanger: true,
    pageSizeOptions: paginationConfig?.PAGE_SIZE_OPTIONS,
    showTotal: showTotal
  });

  /* HANDLERS */
  const handleResetPagination = () => {
    setPagination({
      current: 1,
      pageSize: paginationConfig?.DEFAULT_PAGE_SIZE,
      total: 0,
      showSizeChanger: true,
      pageSizeOptions: paginationConfig?.PAGE_SIZE_OPTIONS,
      showTotal: showTotal
    });
  };

  const handleChangePagination = (pagination: TablePaginationConfig) => {
    const userData = LocalStorageUtil.localStorageGetItem(LocalStorageConstants.USER_DATA) as UserInterface;
    const newUserData = { ...userData };
    if (newUserData?.config?.pagination?.DEFAULT_PAGE_SIZE) {
      newUserData["config"]["pagination"]["DEFAULT_PAGE_SIZE"] = pagination.pageSize!;
      LocalStorageUtil.localStorageSetItem(LocalStorageConstants.USER_DATA, newUserData);
    }

    setPagination({ ...pagination, showTotal: showTotal });
  };

  /* called when data is filtered, to update state with new size of filtered rows */
  const handleChangeTotalSize = (rowLength: number) => {
    const userData = LocalStorageUtil.localStorageGetItem(LocalStorageConstants.USER_DATA) as UserInterface;
    const newUserData = { ...userData };
    if (newUserData?.config?.pagination?.DEFAULT_PAGE_SIZE) {
      newUserData["config"]["pagination"]["DEFAULT_PAGE_SIZE"] = pagination.pageSize!;
      LocalStorageUtil.localStorageSetItem(LocalStorageConstants.USER_DATA, newUserData);
    }

    setPagination((prevState) => {
      return { ...prevState, total: rowLength };
    });
  };

  /* EFFECTS */
  return { pagination, handleChangePagination, handleChangeTotalSize, handleResetPagination };
};

export default useTablePagination;
